import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Spinner } from '@proerp/ui';
import { PortalApiError } from '@/lib/apiClient';
import { isSessionExpired } from '@proerp/shared';
import {
  clearToken,
  currentEndReason,
  getStoredToken,
  takeSessionEndReason,
  type SessionEndReason,
} from '@/lib/auth';
import { useSessionWatch } from '@/lib/useSessionWatch';
import { usePortalUser, PORTAL_ME_KEY } from '@/lib/usePortalUser';
import { ForcePasswordChange } from '@/components/ForcePasswordChange';
import { LoginScreen } from '@/components/LoginScreen';

/**
 * Erklärt der Anmeldeseite, warum sie da ist — sonst steht sie kommentarlos da.
 * Zwei Gründe, zwei Sätze: Um 3 Uhr „12 Stunden ohne Aktivität" zu behaupten
 * wäre falsch (ADR 0106/0107).
 */
const SESSION_END_TEXT: Record<SessionEndReason, string> = {
  idle: 'Deine Sitzung wurde nach 12 Stunden ohne Aktivität beendet. Bitte melde dich neu an.',
  daily: 'Sitzungen enden jede Nacht um 3 Uhr (österreichische Zeit) — deine wurde dabei beendet. Bitte melde dich neu an.',
};

/** Ist der Fehler ein „du bist (nicht mehr) angemeldet"? */
function isAuthFailure(error: unknown): boolean {
  return error instanceof PortalApiError && (error.status === 401 || error.status === 403);
}

/**
 * Entscheidet, ob der Partner die Anmeldung oder das Portal sieht (ADR 0085).
 *
 * Ohne Token wird gar nicht erst gefragt — sonst liefe jeder Kaltstart in ein
 * unnötiges 403. Ein **abgelaufenes** Token zählt dabei wie keins (ADR 0106):
 * Nach 12 h Ruhe ist die Sitzung vorbei, eine Anfrage damit gäbe nur ein 401.
 * Mit gültigem Token entscheidet `/portal/me`; das deckt auch den Fall ab, dass
 * das Token noch gültig aussieht, der Zugang aber deaktiviert wurde.
 *
 * Ein **Netzwerkfehler ist kein Abmeldegrund**: Er bekommt einen eigenen
 * Zustand mit „Erneut versuchen", statt den Partner stillschweigend auf die
 * Anmeldemaske zu werfen und seine Sitzung wegzuwerfen.
 *
 * Steht `mustChangePassword`, endet der Weg hier: Der Partner setzt erst ein
 * eigenes Passwort (ADR 0085). Das gilt für JEDE Seite — die Prüfung sitzt
 * bewusst im Gate und nicht in einer Route, die man umschiffen könnte.
 */
export function PortalGate({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const hasSession = !isSessionExpired(getStoredToken());
  const user = usePortalUser({ enabled: hasSession });
  /**
   * Startwert aus dem letzten Sitzungsende: Nach einem Auto-Logout stünde sonst
   * kommentarlos das Anmeldeformular da. `takeSessionEndReason()` ist je
   * Seitenaufbau idempotent — im StrictMode läuft der Initialisierer doppelt.
   */
  const [notice, setNotice] = useState<string | null>(() => {
    const remembered = takeSessionEndReason();
    if (remembered) return SESSION_END_TEXT[remembered];
    // Token noch da, aber tot: Der Gate entscheidet hier selbst — im Portal gibt
    // es keinen Neuladen-Sprung, über den ein Grund reisen müsste.
    return isSessionExpired(getStoredToken()) ? SESSION_END_TEXT[currentEndReason()] : null;
  });

  // Verlängert bei Aktivität, meldet nach 12 h Ruhe ab (ADR 0106).
  useSessionWatch();

  if (!hasSession || isAuthFailure(user.error)) {
    if (getStoredToken()) clearToken();
    return (
      <LoginScreen
        notice={notice}
        onSuccess={() => {
          setNotice(null);
          void queryClient.invalidateQueries({ queryKey: PORTAL_ME_KEY });
        }}
      />
    );
  }

  if (user.isError) {
    return (
      <Centered>
        <p className="text-sm text-foreground">Das Portal ist gerade nicht erreichbar.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {user.error instanceof Error ? user.error.message : 'Unbekannter Fehler'}
        </p>
        <Button type="button" className="mt-4" onClick={() => void user.refetch()}>
          Erneut versuchen
        </Button>
      </Centered>
    );
  }

  if (user.isPending) {
    return (
      <Centered>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner aria-hidden />
          Deine Anmeldung wird geprüft …
        </p>
      </Centered>
    );
  }

  if (user.data.mustChangePassword) {
    return (
      <ForcePasswordChange
        onDone={() => {
          // Der Wechsel beendet serverseitig alle Sitzungen — mit dem alten
          // Token käme nur noch ein 401. Also sauber abmelden und mit einer
          // Erklärung zur Anmeldung zurück, statt in einen Fehler zu laufen.
          clearToken();
          queryClient.removeQueries({ queryKey: PORTAL_ME_KEY });
          setNotice('Dein Passwort wurde geändert. Bitte melde dich mit dem neuen Passwort an.');
        }}
      />
    );
  }

  return <>{children}</>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div className="text-center">{children}</div>
    </div>
  );
}
